import { useEffect } from "react";
import Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

/**
 * Landing-page motion layer (mounted only on "/").
 *
 *  - Lenis momentum smooth-scrolling, driven by GSAP's ticker and kept in sync
 *    with ScrollTrigger so scroll-bound animations stay aligned with the eased
 *    scroll position. (GSAP's own ScrollSmoother is a paid Club plugin, so Lenis
 *    is the standard free pairing.)
 *  - GSAP scroll-reveal for every [data-reveal] element: items already on screen
 *    ease in immediately with a stagger; the rest play as they scroll into view.
 *
 * Honors prefers-reduced-motion (no smoothing, instant reveal) and tears
 * everything down on unmount, e.g. when navigating to another route.
 */
export default function SmoothScroll() {
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // --- scroll-reveal (scoped to a gsap.context for one-call cleanup) ---
    const ctx = gsap.context(() => {
      const els = gsap.utils.toArray<HTMLElement>("[data-reveal]");
      if (!els.length) return;

      if (reduced) {
        gsap.set(els, { opacity: 1, y: 0 });
        return;
      }

      const vh = window.innerHeight;
      let onscreen = 0;
      els.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top >= vh * 0.92) {
          // Below the fold → reveal as it scrolls into view.
          ScrollTrigger.create({
            trigger: el,
            start: "top 88%",
            once: true,
            onEnter: () => gsap.to(el, { opacity: 1, y: 0, duration: 0.85, ease: "power3.out" }),
          });
        } else if (rect.bottom > 0) {
          // Visible on load → ease in now, lightly staggered.
          gsap.to(el, { opacity: 1, y: 0, duration: 0.85, ease: "power3.out", delay: Math.min(onscreen, 6) * 0.08 });
          onscreen += 1;
        } else {
          // Already scrolled past (e.g. a refresh mid-page) → just show it.
          gsap.set(el, { opacity: 1, y: 0 });
        }
      });
    });

    // --- Lenis smooth scroll (skipped under reduced-motion) ---
    let lenis: Lenis | null = null;
    let onTick: ((time: number) => void) | null = null;
    let onAnchor: ((e: MouseEvent) => void) | null = null;

    if (!reduced) {
      lenis = new Lenis({
        duration: 1.05,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // expo-out
        smoothWheel: true,
        touchMultiplier: 1.5,
      });
      const l = lenis;
      l.on("scroll", ScrollTrigger.update);
      onTick = (time) => l.raf(time * 1000);
      gsap.ticker.add(onTick);
      gsap.ticker.lagSmoothing(0);

      // Glide in-page anchors (#mission, #registries, …) through Lenis instead of
      // jumping, clearing the fixed nav.
      onAnchor = (e) => {
        const link = (e.target as HTMLElement)?.closest?.('a[href^="#"]') as HTMLAnchorElement | null;
        if (!link) return;
        const href = link.getAttribute("href");
        if (!href || href === "#") return;
        const target = document.querySelector(href);
        if (!target) return;
        e.preventDefault();
        l.scrollTo(target as HTMLElement, { offset: -90 });
      };
      document.addEventListener("click", onAnchor);
    }

    // Recompute trigger positions once fonts / 3D models / the boot splash settle.
    const refreshId = window.setTimeout(() => ScrollTrigger.refresh(), 500);

    return () => {
      window.clearTimeout(refreshId);
      if (onAnchor) document.removeEventListener("click", onAnchor);
      if (onTick) gsap.ticker.remove(onTick);
      if (lenis) lenis.destroy();
      ctx.revert();
    };
  }, []);

  return null;
}
